import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBolt,
  FaExclamationTriangle,
  FaEyeSlash,
  FaGhost,
  FaHeartbeat,
  FaHourglassHalf,
  FaPuzzlePiece,
  FaSkull,
  FaVolumeUp
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const heroImage = '/dead-inside-hero.png';
const deviceImage = '/dead-inside-device.png';
const iconImage = '/dead-inside-icon.png';

interface RealBloodDrop {
  x: number;
  y: number;
  w: number;
  length: number;
  vy: number;
  vx: number;
  alpha: number;
  swaySpeed: number;
}

interface RealBloodDrip {
  x: number;
  y: number;
  speed: number;
  width: number;
  length: number;
  maxLength: number;
  alpha: number;
  wobbleOffset: number;
}

interface RealSplatter {
  x: number;
  y: number;
  alpha: number;
  polygon: { dx: number; dy: number }[];
  corePolygon: { dx: number; dy: number }[];
  streaks: { x1: number; y1: number; x2: number; y2: number; w: number }[];
}

const featureCards = [
  {
    icon: FaBolt,
    title: 'Heart-Stopping Jump Scares',
    highlight: 'Pure Adrenaline',
    description:
      'Unpredictable corner ambushes, slamming doors, and horrifying face-to-face jumpscares when stalkers lunge from the darkness.'
  },
  {
    icon: FaEyeSlash,
    title: 'Hide & Seek Stealth',
    highlight: 'Silence is Survival',
    description:
      'Duck into dark wardrobes, slip under beds, and crouch behind decrepit furniture. Break sightlines before the chase turns lethal.'
  },
  {
    icon: FaPuzzlePiece,
    title: 'Intricate Escape Puzzles',
    highlight: 'Brain Under Pressure',
    description:
      'Scavenge dark rooms for keys, fuses, wire cutters, and combination codes to dismantle house locks and open secret exits.'
  },
  {
    icon: FaSkull,
    title: 'Multiple Enemy Variants',
    highlight: 'Unforgiving AI',
    description:
      'Confront terrifying stalker variants with unique speeds, patrol patterns, and relentless 30-second pursuit routines.'
  },
  {
    icon: FaVolumeUp,
    title: '20M Sound Detection',
    highlight: 'Acoustic Threat',
    description:
      'The house listens. Dropping an object, stepping on squeaky floorboards, or running alerts enemies within a 20-meter acoustic radius.'
  },
  {
    icon: FaHourglassHalf,
    title: '3 Days Survival Quota',
    highlight: 'Limited Chances',
    description:
      'Each capture knocks you unconscious and claims a day. Escape before Day 3 ends, or remain trapped inside forever.'
  }
];

const survivalRules = [
  {
    step: 'I',
    title: 'Never Turn Your Back on Open Doors',
    description: 'Enemies patrol silently and trigger sudden ambush jumpscares from dark corridors.'
  },
  {
    step: 'II',
    title: 'Sound Equals Death',
    description: 'Throw objects as distractions to lead stalkers away from essential puzzle doors.'
  },
  {
    step: 'III',
    title: 'Check Wardrobes & Closets',
    description: 'Hide immediately when the heartbeat rises. Wait until heavy footsteps fade away.'
  },
  {
    step: 'IV',
    title: 'Assemble the Escape Route',
    description: 'Solve the main door locks, electric fuses, and attic puzzle pieces before your days expire.'
  }
];

// Helper to generate organic non-circular splatter polygons
const generateOrganicSplatter = (baseRadius: number) => {
  const points: { dx: number; dy: number }[] = [];
  const corePoints: { dx: number; dy: number }[] = [];
  const streaks: { x1: number; y1: number; x2: number; y2: number; w: number }[] = [];

  const numPoints = 28;
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * Math.PI * 2;
    // Irregular wavy radius (never round)
    let r = baseRadius * (0.45 + Math.sin(angle * 4) * 0.35 + Math.random() * 0.4);
    // Tendril spikes
    if (Math.random() < 0.28) {
      r += baseRadius * (0.6 + Math.random() * 0.9);
    }
    const dx = Math.cos(angle) * r;
    const dy = Math.sin(angle) * r;
    points.push({ dx, dy });
    corePoints.push({ dx: dx * 0.55, dy: dy * 0.55 });
  }

  // Radiating spray streaks (like blood flung by an impact)
  const streakCount = 4 + Math.floor(Math.random() * 6);
  for (let s = 0; s < streakCount; s++) {
    const a = Math.random() * Math.PI * 2;
    const dist1 = baseRadius * (0.9 + Math.random() * 0.5);
    const dist2 = dist1 + baseRadius * (0.8 + Math.random() * 1.4);
    streaks.push({
      x1: Math.cos(a) * dist1,
      y1: Math.sin(a) * dist1,
      x2: Math.cos(a) * dist2,
      y2: Math.sin(a) * dist2,
      w: 1.2 + Math.random() * 2.5
    });
  }

  return { points, corePoints, streaks };
};

const DeadInside: React.FC = () => {
  const [jumpscareActive, setJumpscareActive] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const splattersRef = useRef<RealSplatter[]>([]);

  // Trigger kill jump scare with violent blood burst
  const triggerJumpscare = () => {
    setJumpscareActive(true);

    const canvas = canvasRef.current;
    if (canvas) {
      // Spawn violent impact splatters across screen
      for (let i = 0; i < 9; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const baseRadius = 25 + Math.random() * 45;
        const { points, corePoints, streaks } = generateOrganicSplatter(baseRadius);

        splattersRef.current.push({
          x,
          y,
          alpha: 0.95,
          polygon: points,
          corePolygon: corePoints,
          streaks
        });
      }
    }

    setTimeout(() => {
      setJumpscareActive(false);
    }, 1800);
  };

  // Real Fluid Blood Drops & Dripping Trails Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initial Real Teardrop Blood Droplets (NOT circles)
    const bloodDrops: RealBloodDrop[] = [];
    const dropCount = 55;
    for (let i = 0; i < dropCount; i++) {
      bloodDrops.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        w: 2.0 + Math.random() * 3.5,
        length: 12 + Math.random() * 22,
        vy: 2.2 + Math.random() * 4.0,
        vx: (Math.random() - 0.5) * 0.4,
        alpha: 0.45 + Math.random() * 0.45,
        swaySpeed: 1.5 + Math.random() * 2.0
      });
    }

    // Vertical Wavy Blood Drip Streaks (slowly creeping down like blood on glass)
    const bloodDrips: RealBloodDrip[] = [];
    const dripCount = 14;
    for (let i = 0; i < dripCount; i++) {
      bloodDrips.push({
        x: Math.random() * canvas.width,
        y: Math.random() * (canvas.height * 0.25),
        speed: 0.4 + Math.random() * 0.8,
        width: 2.2 + Math.random() * 3.8,
        length: 0,
        maxLength: 90 + Math.random() * 260,
        alpha: 0.55 + Math.random() * 0.4,
        wobbleOffset: Math.random() * 10
      });
    }

    // Ambient initial organic splatters
    if (splattersRef.current.length === 0) {
      for (let i = 0; i < 6; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const baseRadius = 20 + Math.random() * 30;
        const { points, corePoints, streaks } = generateOrganicSplatter(baseRadius);
        splattersRef.current.push({
          x,
          y,
          alpha: 0.45 + Math.random() * 0.3,
          polygon: points,
          corePolygon: corePoints,
          streaks
        });
      }
    }

    let animationId: number;
    let time = 0;

    const render = () => {
      time += 0.02;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 1. Render Real Organic Blood Splatters (Non-Circular Jagged Shapes)
      for (let i = splattersRef.current.length - 1; i >= 0; i--) {
        const s = splattersRef.current[i];

        // Outer jagged blood blotch
        ctx.beginPath();
        for (let j = 0; j < s.polygon.length; j++) {
          const pt = s.polygon[j];
          if (j === 0) ctx.moveTo(s.x + pt.dx, s.y + pt.dy);
          else ctx.lineTo(s.x + pt.dx, s.y + pt.dy);
        }
        ctx.closePath();
        ctx.fillStyle = `rgba(135, 7, 15, ${s.alpha})`;
        ctx.fill();

        // Dark coagulated clot core
        ctx.beginPath();
        for (let j = 0; j < s.corePolygon.length; j++) {
          const pt = s.corePolygon[j];
          if (j === 0) ctx.moveTo(s.x + pt.dx, s.y + pt.dy);
          else ctx.lineTo(s.x + pt.dx, s.y + pt.dy);
        }
        ctx.closePath();
        ctx.fillStyle = `rgba(55, 2, 5, ${s.alpha * 0.95})`;
        ctx.fill();

        // Radiating spray streaks
        for (const st of s.streaks) {
          ctx.beginPath();
          ctx.moveTo(s.x + st.x1, s.y + st.y1);
          ctx.lineTo(s.x + st.x2, s.y + st.y2);
          ctx.lineWidth = st.w;
          ctx.strokeStyle = `rgba(165, 10, 22, ${s.alpha * 0.85})`;
          ctx.lineCap = 'round';
          ctx.stroke();
        }

        // Slowly dry / fade kill splatters
        if (splattersRef.current.length > 7) {
          s.alpha -= 0.0007;
          if (s.alpha <= 0.12) {
            splattersRef.current.splice(i, 1);
          }
        }
      }

      // 2. Render Creeping Blood Streaks (Running Down Glass)
      for (const drip of bloodDrips) {
        drip.length += drip.speed;

        ctx.beginPath();
        ctx.moveTo(drip.x, drip.y);
        // Subtle organic wobble
        const midY = drip.y + drip.length * 0.5;
        const midX = drip.x + Math.sin(time * 2 + drip.wobbleOffset) * 2.5;
        const endY = drip.y + drip.length;
        ctx.quadraticCurveTo(midX, midY, drip.x, endY);
        ctx.lineWidth = drip.width;
        ctx.strokeStyle = `rgba(145, 8, 16, ${drip.alpha})`;
        ctx.lineCap = 'round';
        ctx.stroke();

        // Hanging teardrop bead at tip (NOT a circle)
        const bw = drip.width * 1.3;
        ctx.beginPath();
        ctx.moveTo(drip.x, endY - bw);
        ctx.bezierCurveTo(drip.x - bw * 1.5, endY + bw * 0.3, drip.x - bw * 0.8, endY + bw * 2.0, drip.x, endY + bw * 2.2);
        ctx.bezierCurveTo(drip.x + bw * 0.8, endY + bw * 2.0, drip.x + bw * 1.5, endY + bw * 0.3, drip.x, endY - bw);
        ctx.closePath();
        ctx.fillStyle = `rgba(185, 12, 22, ${drip.alpha})`;
        ctx.fill();

        if (drip.length >= drip.maxLength) {
          drip.x = Math.random() * canvas.width;
          drip.y = Math.random() * (canvas.height * 0.25);
          drip.length = 0;
          drip.maxLength = 90 + Math.random() * 260;
        }
      }

      // 3. Render Real Teardrop Fluid Blood Drops (Tapered Top, Bulbous Bottom - NO CIRCLES)
      for (const drop of bloodDrops) {
        drop.y += drop.vy;
        drop.x += drop.vx + Math.sin(time * drop.swaySpeed + drop.y * 0.015) * 0.35;

        const w = drop.w;
        const len = drop.length;

        // Fluid teardrop body
        ctx.beginPath();
        ctx.moveTo(drop.x, drop.y - len);
        ctx.bezierCurveTo(drop.x - w * 0.5, drop.y - len * 0.3, drop.x - w * 1.5, drop.y + w * 0.4, drop.x, drop.y + w * 1.3);
        ctx.bezierCurveTo(drop.x + w * 1.5, drop.y + w * 0.4, drop.x + w * 0.5, drop.y - len * 0.3, drop.x, drop.y - len);
        ctx.closePath();
        ctx.fillStyle = `rgba(185, 12, 24, ${drop.alpha})`;
        ctx.fill();

        // Deep red coagulated center
        ctx.beginPath();
        ctx.moveTo(drop.x, drop.y - len * 0.6);
        ctx.bezierCurveTo(drop.x - w * 0.3, drop.y - len * 0.15, drop.x - w * 0.9, drop.y + w * 0.2, drop.x, drop.y + w * 0.9);
        ctx.bezierCurveTo(drop.x + w * 0.9, drop.y + w * 0.2, drop.x + w * 0.3, drop.y - len * 0.15, drop.x, drop.y - len * 0.6);
        ctx.closePath();
        ctx.fillStyle = `rgba(80, 4, 8, ${drop.alpha * 0.95})`;
        ctx.fill();

        // Reset if off bottom
        if (drop.y > canvas.height + 30) {
          drop.y = -30;
          drop.x = Math.random() * canvas.width;
        }
        if (drop.x < -30) drop.x = canvas.width + 30;
        if (drop.x > canvas.width + 30) drop.x = -30;
      }

      animationId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const deadInsideJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: 'Dead Inside',
    url: `${siteUrl}/dead-inside`,
    image: `${siteUrl}${heroImage}`,
    gamePlatform: 'Android',
    applicationCategory: 'Game',
    genre: ['Survival Horror', 'Jump Scare', 'Puzzle', 'Stealth'],
    description:
      'Dead Inside is a Granny-style mobile survival horror game featuring terrifying jump scares, hide-and-seek stealth mechanics, room escape puzzles, and intelligent roaming enemies.'
  };

  return (
    <div className={`relative min-h-screen overflow-hidden bg-black text-gray-200 ${jumpscareActive ? 'animate-jumpscare-shake' : ''}`}>
      <SEO
        title="Dead Inside - Granny Style Horror Game with Jump Scares | RedRender"
        description="Dead Inside is a Granny-inspired mobile survival horror game in development by RedRender. Features intense jump scares, hide-and-seek stealth, room escape puzzles, and terrifying enemies."
        path="/dead-inside"
        image={heroImage}
        keywords={[
          'Dead Inside',
          'Dead Inside horror game',
          'jump scare mobile game',
          'Granny style horror game',
          'mobile horror game',
          'Android survival horror',
          'hide and seek horror',
          'escape room horror',
          'RedRender game'
        ]}
        jsonLd={deadInsideJsonLd}
      />

      {/* Layer 1: Photorealistic Gritty Dark Horror Wall with Wet Blood Splatters & Drips */}
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-cover bg-center bg-fixed opacity-45 mix-blend-screen"
        style={{ backgroundImage: `url('/dead-inside-blood-bg.jpg')` }}
      />

      {/* Layer 2: Authentic Screen Edge Blood Splatter & Dripping Overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-cover bg-center opacity-65 mix-blend-screen"
        style={{ backgroundImage: `url('/blood-screen-splatter.png')` }}
      />

      {/* Layer 3: Dynamic Real Fluid Blood Drops & Dripping Canvas (NO circles) */}
      <canvas
        ref={canvasRef}
        className="pointer-events-none fixed inset-0 z-10 h-full w-full opacity-85"
      />

      {/* Dripping Blood Curtain Top Border (SVG) */}
      <div className="pointer-events-none fixed top-0 left-0 right-0 z-20 overflow-hidden leading-none opacity-90">
        <svg
          viewBox="0 0 1200 80"
          preserveAspectRatio="none"
          className="h-12 w-full text-red-950 fill-current drop-shadow-[0_4px_16px_rgba(180,10,20,0.85)]"
        >
          <path d="M0,0 L1200,0 L1200,15 Q1175,55 1150,18 Q1120,75 1090,22 Q1050,90 1020,16 Q980,60 950,28 Q910,85 880,18 Q840,65 810,24 Q770,95 740,16 Q700,55 670,20 Q630,90 600,28 Q560,55 530,16 Q490,95 460,24 Q420,65 390,20 Q350,90 320,16 Q280,60 250,28 Q210,95 180,20 Q140,65 110,24 Q70,85 40,16 Q15,45 0,20 Z" />
        </svg>
      </div>

      {/* Screen Border Blood Damage Vignette (Corner Splatters) */}
      <div className="pointer-events-none fixed inset-0 z-20 shadow-[inset_0_0_100px_rgba(160,8,16,0.6),inset_0_0_180px_rgba(80,2,5,0.9)]" />

      {/* Kill / Jump Scare Screen Overlay with Blood Spray */}
      {jumpscareActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-b from-red-950 via-black to-red-950/95 backdrop-blur-md animate-pulse pointer-events-none">
          {/* Blood Wash Vignette */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(220,20,30,0.7)_0%,rgba(90,4,8,0.98)_70%)] animate-ping" style={{ animationDuration: '0.8s' }} />

          <div className="relative text-center px-4">
            <FaSkull className="mx-auto text-8xl sm:text-9xl text-red-600 animate-spin" style={{ animationDuration: '0.5s' }} />
            <h1 className="mt-4 font-horror text-7xl sm:text-9xl text-red-500 text-horror-blood animate-bounce">
              YOU WERE KILLED!
            </h1>
            <p className="mt-2 font-horror text-3xl sm:text-5xl text-yellow-300 tracking-wider">
              DAY I HAS ENDED
            </p>
            <p className="mt-3 font-display text-sm sm:text-lg uppercase tracking-widest text-red-200">
              The stalker dragged you back into the dark...
            </p>
          </div>
        </div>
      )}

      {/* Atmospheric Creepy Ambient Glows */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_15%,rgba(220,38,38,0.2),transparent_35rem),radial-gradient(circle_at_15%_55%,rgba(153,27,27,0.15),transparent_30rem),radial-gradient(circle_at_85%_65%,rgba(185,28,28,0.15),transparent_32rem)]" />
      <div className="pointer-events-none absolute -left-28 top-20 h-[32rem] w-[32rem] rounded-full bg-red-600/15 blur-[140px] animate-horror-flicker" />
      <div className="pointer-events-none absolute -right-28 top-[28rem] h-[36rem] w-[36rem] rounded-full bg-red-950/30 blur-[150px]" />

      {/* Hero Section */}
      <section className="relative pt-24 z-20">
        <div className="container relative z-10 mx-auto px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            {/* Left Content */}
            <div className="animate-slide-up">
              {/* Status Badge & Game Icon */}
              <div className="flex flex-wrap items-center gap-4">
                <img
                  src={iconImage}
                  alt="Dead Inside Game Icon"
                  className="h-12 w-12 rounded-2xl border-2 border-red-600/70 shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                />
                <div className="inline-flex items-center gap-2.5 rounded-full border border-red-500/50 bg-red-950/50 px-5 py-2 backdrop-blur-md shadow-[0_0_25px_rgba(239,68,68,0.3)]">
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-80" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
                  </span>
                  <span className="font-display text-xs sm:text-sm font-bold uppercase tracking-wider text-red-300">
                    Work in Progress • Mobile Horror Game
                  </span>
                </div>
              </div>

              {/* Horror Main Title */}
              <h1 className="mt-6 font-horror text-6xl sm:text-7xl lg:text-8xl tracking-wider text-red-600 text-horror-blood animate-horror-pulse">
                DEAD INSIDE
              </h1>

              {/* Subheading */}
              <p className="mt-2 font-horror text-2xl sm:text-3xl lg:text-4xl tracking-wide text-gray-200">
                Survive. Hide. Escape.
              </p>

              {/* Storyline Description */}
              <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-gray-300">
                Trapped inside a decaying house where every squeaking floorboard echoes in the dark.
                Inspired by classic <strong className="text-red-400">Granny-style survival horror</strong>,{' '}
                <strong className="text-white">Dead Inside</strong> delivers intense{' '}
                <span className="text-red-400 font-semibold">Jump Scares</span>, hide-and-seek stealth, room escape puzzles,
                and violent stalker encounters.
              </p>

              {/* Jumpscare Alert Bar */}
              <div className="mt-8 flex items-center gap-3 rounded-2xl border border-red-600/40 bg-red-950/30 p-4 backdrop-blur-md">
                <FaExclamationTriangle className="text-2xl text-red-500 flex-shrink-0 animate-pulse" />
                <div className="text-sm">
                  <span className="font-bold text-red-400 uppercase tracking-wide">Warning: Intense Horror Ahead — </span>
                  <span className="text-gray-300">Features sudden ambush jumpscares, violent kill screens, and psychological tension.</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={triggerJumpscare}
                  className="group relative inline-flex items-center justify-center gap-3 rounded-full border border-red-500 bg-gradient-to-r from-red-600 via-red-700 to-rose-900 px-8 py-4 font-semibold text-white shadow-[0_0_35px_rgba(239,68,68,0.4)] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(239,68,68,0.6)] cursor-pointer"
                >
                  <FaBolt className="text-yellow-300 animate-bounce" />
                  <span>Test Kill Jump Scare</span>
                </button>
                <Link
                  to="/portfolio"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/20 bg-white/5 px-8 py-4 font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-red-500/50 hover:bg-red-950/30"
                >
                  All Projects
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-red-500/30 bg-red-950/20 px-8 py-4 font-semibold text-red-300 backdrop-blur-sm transition-all duration-300 hover:border-red-500/60"
                >
                  Contact Us
                </Link>
              </div>

              {/* Quick Feature Badges */}
              <div className="mt-8 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
                {['Jump Scares', 'Hide & Seek', 'Room Puzzles', '3 Days Quota'].map((tag) => (
                  <div
                    key={tag}
                    className="rounded-2xl border border-red-900/40 bg-red-950/30 px-3 py-3 text-center text-xs font-bold uppercase tracking-wider text-red-300 backdrop-blur-sm hover:border-red-500/60 transition-colors"
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Centered Poster Art with Blood Splatter Frame */}
            <div className="animate-scale-in">
              <div className="relative overflow-hidden rounded-[2.2rem] border-2 border-red-600/50 bg-black p-2.5 shadow-[0_0_50px_rgba(239,68,68,0.35)] backdrop-blur-xl animate-horror-border">
                <div className="relative overflow-hidden rounded-[1.8rem] bg-black">
                  <img
                    src={heroImage}
                    alt="Dead Inside Official Horror Key Art Poster"
                    className="w-full h-auto object-contain transition-transform duration-700 hover:scale-[1.02]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jump Scare & Horror Tension Showcase */}
      <section className="relative py-16 sm:py-20 border-t border-red-900/30 bg-gradient-to-b from-black via-red-950/20 to-black z-20">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] border border-red-600/40 bg-gradient-to-br from-red-950/50 via-black to-red-950/30 p-8 sm:p-12 lg:p-16 shadow-2xl">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-950/40 px-4 py-1.5 backdrop-blur-sm">
                  <FaHeartbeat className="text-red-400 animate-pulse" />
                  <span className="font-display text-xs font-bold uppercase tracking-widest text-red-300">
                    High Tension Engine
                  </span>
                </div>
                <h2 className="mt-6 font-horror text-4xl sm:text-5xl lg:text-6xl text-red-500 tracking-wide">
                  UNEXPECTED JUMP SCARES
                </h2>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-300">
                  Every corner in <strong className="text-white">Dead Inside</strong> hides a deadly surprise.
                  Doors can swing open without warning. Stalkers emerge from shadowed hallways with screeching audio cues that will make you drop your phone.
                </p>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-red-900/40 bg-black/70 p-4">
                    <p className="font-horror text-2xl text-red-400">AMBUSH EVENTS</p>
                    <p className="text-xs text-gray-400 mt-1">Sudden enemy corner lunges</p>
                  </div>
                  <div className="rounded-2xl border border-red-900/40 bg-black/70 p-4">
                    <p className="font-horror text-2xl text-red-400">HEARTBEAT AUDIO</p>
                    <p className="text-xs text-gray-400 mt-1">Dynamic proximity tension</p>
                  </div>
                  <div className="rounded-2xl border border-red-900/40 bg-black/70 p-4">
                    <p className="font-horror text-2xl text-red-400">20M DETECTION</p>
                    <p className="text-xs text-gray-400 mt-1">Sensitive sound listeners</p>
                  </div>
                  <div className="rounded-2xl border border-red-900/40 bg-black/70 p-4">
                    <p className="font-horror text-2xl text-red-400">30S CHASE</p>
                    <p className="text-xs text-gray-400 mt-1">Relentless tracking loop</p>
                  </div>
                </div>
              </div>

              {/* In-Game Mobile Simulator Screen */}
              <div className="relative overflow-hidden rounded-2xl border border-red-500/40 bg-black p-3 shadow-2xl">
                <img
                  src={deviceImage}
                  alt="Dead Inside Unity 6 Mobile Simulator Gameplay View"
                  className="w-full h-auto rounded-xl object-contain"
                />
                <div className="mt-3 text-center">
                  <p className="font-horror text-lg text-red-400 tracking-wider">
                    UNITY 6 IN-GAME MOBILE SIMULATOR
                  </p>
                  <p className="text-xs text-gray-400">Touch Controls • Mobile URP Lighting • Real-Time AI</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Horror Gameplay Pillars */}
      <section className="relative py-16 sm:py-24 z-20">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
              <FaGhost className="text-red-400" />
              <span className="font-display text-sm font-semibold text-red-400">Survival Mechanics</span>
            </div>
            <h2 className="mt-6 font-horror text-5xl sm:text-6xl lg:text-7xl text-red-500 text-horror-blood">
              RULES OF THE HOUSE
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-gray-400 text-lg">
              Outsmarting the horror requires nerves of steel. Master these core mechanics to have any hope of escape.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featureCards.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-3xl border border-red-900/30 bg-gradient-to-br from-red-950/20 via-black to-red-950/10 p-8 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/60 hover:shadow-2xl hover:shadow-red-950/60 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/0 to-red-900/0 opacity-0 transition-opacity duration-500 group-hover:from-red-600/10 group-hover:to-red-900/20 group-hover:opacity-100" />
                <div className="inline-flex p-4 rounded-2xl bg-red-950/50 border border-red-500/30 text-red-400 group-hover:border-red-500 group-hover:scale-110 transition-all duration-300">
                  <feature.icon className="text-3xl" />
                </div>
                <span className="ml-3 inline-block rounded-full bg-red-900/40 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-300">
                  {feature.highlight}
                </span>
                <h3 className="relative mt-6 font-horror text-3xl font-bold text-white group-hover:text-red-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="relative mt-3 leading-relaxed text-gray-400 text-sm sm:text-base">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Survival Rules */}
      <section className="relative py-16 sm:py-20 border-t border-red-900/30 z-20">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-red-400">Tombstone Directives</span>
              </div>
              <h2 className="mt-6 font-horror text-5xl sm:text-6xl text-red-500">
                HOW TO ESCAPE ALIVE
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-400">
                Panic triggers mistakes. Keep your flashlight off when enemies are near, listen for footsteps, and memorize safe wardrobe hiding spots.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {survivalRules.map((rule) => (
                <div
                  key={rule.step}
                  className="rounded-3xl border border-red-900/30 bg-gradient-to-br from-red-950/20 via-black to-black p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-red-500/50"
                >
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/40 bg-red-950/50 font-horror text-2xl font-bold text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
                    {rule.step}
                  </div>
                  <h3 className="font-horror text-2xl text-white">{rule.title}</h3>
                  <p className="mt-2 text-sm text-gray-400 leading-relaxed">{rule.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="relative py-16 pb-24 border-t border-red-900/30 z-20">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] border-2 border-red-600/50 bg-gradient-to-br from-red-950/60 via-black to-red-950/40 px-6 py-14 text-center shadow-[0_0_60px_rgba(239,68,68,0.3)] backdrop-blur-xl sm:px-12 sm:py-18">
            <FaSkull className="mx-auto text-6xl text-red-500 animate-pulse" />
            <h2 className="mt-6 font-horror text-5xl sm:text-6xl lg:text-7xl text-red-500 text-horror-blood">
              PREPARE FOR THE NIGHTMARE
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-gray-300">
              <strong className="text-white">Dead Inside</strong> is currently in active development for Android by RedRender.
              Follow our dev logs or get in touch to collaborate.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-red-600 to-rose-800 px-8 py-4 font-semibold text-white shadow-lg shadow-red-600/40 hover:shadow-red-600/70 hover:scale-105 transition-all duration-300"
              >
                <span>Connect With RedRender</span>
                <FaArrowRight />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-4 font-semibold text-gray-200 hover:bg-white/10 hover:text-white transition-all duration-300"
              >
                <span>Browse All Games</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeadInside;
